import { Navigate } from 'react-router-dom';
import facultyComponents from '../../../../../config/semantic-components/faculty-1.0.json';
import facultyRoutesConfig from '../../../../../config/semantic-routes/faculty.json';
import { FacultyLiveSurface } from './FacultySemanticSurface';
import {
  SemanticRouteAdapter,
  type SemanticComponentsConfig,
  type SemanticRoutesConfig,
} from '../SemanticRouteAdapter';

const componentsConfig = facultyComponents as SemanticComponentsConfig;
const routesConfig = facultyRoutesConfig as SemanticRoutesConfig;

const FACULTY_DIRECTORY_PATH = '/school/faculty';
const FACULTY_TAB_SUFFIX: Record<string, string | null> = {
  'faculty-profile': null,
  'faculty-profile-teaching': 'teaching',
  'faculty-profile-notes': 'notes',
  'faculty-profile-communications': 'communications',
};

const normalizePath = (pathname: string): string => pathname.replace(/\/+$/, '') || '/';

const modeFromComponentId = (
  componentId: string,
): 'directory' | 'profile' | 'teaching' | 'notes' | 'communications' => {
  if (componentId === 'faculty-directory') return 'directory';
  if (componentId === 'faculty-profile-teaching') return 'teaching';
  if (componentId === 'faculty-profile-notes') return 'notes';
  if (componentId === 'faculty-profile-communications') return 'communications';
  return 'profile';
};

const mapCanonicalFacultyPath = (pathname: string | null): string | null => {
  if (!pathname) return pathname;
  const normalized = normalizePath(pathname);
  if (normalized === '/school/faculty' || normalized === '/school/faculty/directory') {
    return '/admin/faculty/directory';
  }

  const schoolProfileMatch = normalized.match(
    /^\/school\/faculty\/profile\/([^/]+?)(?:\/(teaching|notes|communications))?$/,
  );
  if (schoolProfileMatch?.[1]) {
    if (schoolProfileMatch[2] === 'teaching') return '/admin/faculty/profile/teaching';
    if (schoolProfileMatch[2] === 'notes') return '/admin/faculty/profile/notes';
    if (schoolProfileMatch[2] === 'communications') return '/admin/faculty/profile/communications';
    return '/admin/faculty/profile';
  }

  const legacySchoolProfileMatch = normalized.match(
    /^\/school\/faculty\/([^/]+?)(?:\/(teaching-assignment|notes|communications))?$/,
  );
  if (legacySchoolProfileMatch?.[1] && !['directory', 'profile'].includes(legacySchoolProfileMatch[1])) {
    if (legacySchoolProfileMatch[2] === 'teaching-assignment') return '/admin/faculty/profile/teaching';
    if (legacySchoolProfileMatch[2] === 'notes') return '/admin/faculty/profile/notes';
    if (legacySchoolProfileMatch[2] === 'communications') return '/admin/faculty/profile/communications';
    return '/admin/faculty/profile';
  }

  if (normalized === '/school/faculty/profile') return '/admin/faculty/profile';
  if (normalized === '/school/faculty/profile/teaching') return '/admin/faculty/profile/teaching';
  if (normalized === '/school/faculty/profile/notes') return '/admin/faculty/profile/notes';
  if (normalized === '/school/faculty/profile/communications') return '/admin/faculty/profile/communications';

  if (normalized === '/admin/faculty' || normalized === '/admin/faculty/directory') {
    return '/admin/faculty/directory';
  }
  const adminProfileMatch = normalized.match(
    /^\/admin\/faculty\/([^/]+?)(?:\/(teaching-assignment|notes|communications))?$/,
  );
  if (adminProfileMatch?.[1] && !['directory', 'profile'].includes(adminProfileMatch[1])) {
    if (adminProfileMatch[2] === 'teaching-assignment') return '/admin/faculty/profile/teaching';
    if (adminProfileMatch[2] === 'notes') return '/admin/faculty/profile/notes';
    if (adminProfileMatch[2] === 'communications') return '/admin/faculty/profile/communications';
    return '/admin/faculty/profile';
  }
  return pathname;
};

const readFacultyIdFromParams = (params: Record<string, string | undefined>): string | null => {
  const directFacultyId = params.facultyId;
  if (directFacultyId) return decodeURIComponent(directFacultyId);

  const wildcard = params['*'] || '';
  const [first, second] = wildcard.split('/');
  if (first === 'profile' && second) {
    return decodeURIComponent(second);
  }
  if (first && !['directory', 'profile'].includes(first)) {
    return decodeURIComponent(first);
  }
  if (typeof window !== 'undefined') {
    const search = new URLSearchParams(window.location.search);
    const fromQuery = search.get('facultyId');
    if (fromQuery) return fromQuery;
  }
  return null;
};

const resolveFacultyInternalPath = (componentId: string, params: Record<string, string | undefined>): string => {
  if (componentId === 'faculty-directory') {
    return FACULTY_DIRECTORY_PATH;
  }

  const facultyId = readFacultyIdFromParams(params);
  if (!facultyId) {
    return FACULTY_DIRECTORY_PATH;
  }

  const suffix = FACULTY_TAB_SUFFIX[componentId];
  const encoded = encodeURIComponent(facultyId);
  return suffix
    ? `${FACULTY_DIRECTORY_PATH}/profile/${encoded}/${suffix}`
    : `${FACULTY_DIRECTORY_PATH}/profile/${encoded}`;
};

export function FacultySemanticRoute() {
  return (
    <div data-extracted-from="owned:ystnj/semantic/faculty-route">
      <SemanticRouteAdapter
        moduleKey="faculty"
        componentsConfig={componentsConfig}
        routesConfig={routesConfig}
        mapIntegrationPath={mapCanonicalFacultyPath}
        internal={{
          resolvePath: (componentId, params) => resolveFacultyInternalPath(componentId, params),
          render: ({ componentId, internalPath, pathname }) => {
            if (normalizePath(pathname) !== normalizePath(internalPath)) {
              return <Navigate to={internalPath} replace />;
            }
            return <FacultyLiveSurface mode={modeFromComponentId(componentId)} />;
          },
        }}
      />
    </div>
  );
}
